import { ResultHighlight, SearchDocument, SearchResult } from '@backstage/plugin-search-common';
import { HighlightedSearchResultText, createSearchResultListItemExtension } from '@backstage/plugin-search-react';
import { searchPlugin } from '@backstage/plugin-search';
import { Link } from '@backstage/core-components';
import ListItemText from '@material-ui/core/ListItemText';

type ProvisionedSearchDocument = SearchDocument & {
  provisioned_resource_ref?: string;
  marketplace_product_id?: string;
  kind?: string;
};

type Props = {
  result?: SearchDocument;
  highlight?: ResultHighlight;
  noTrack?: boolean;
};

function isSuppressed(doc: ProvisionedSearchDocument): boolean {
  // Suppress entities already shown correctly in Marketplace or Meus Recursos
  if (doc.provisioned_resource_ref) return true;
  if (doc.marketplace_product_id) return true;
  // Templates only make sense in Marketplace/Scaffold context — suppress all
  if (doc.kind === 'Template') return true;
  // Location entities are catalog internals — never useful in search
  if (doc.kind === 'Location') return true;
  return false;
}

function ProvisionedResourcesSearchResultListItemComponent({ result, highlight, noTrack }: Props) {
  if (!result) return null;

  const doc = result as ProvisionedSearchDocument;
  if (isSuppressed(doc)) return null;

  // Only render provisioned-resources type — catalog items handled by CatalogSearchResultListItem
  if (!result.location.startsWith('/provisioned-resources/')) return null;

  const title = highlight?.fields.title
    ? <HighlightedSearchResultText text={highlight.fields.title} preTag={highlight.preTag} postTag={highlight.postTag} />
    : result.title;

  const description = highlight?.fields.text
    ? <HighlightedSearchResultText text={highlight.fields.text} preTag={highlight.preTag} postTag={highlight.postTag} />
    : result.text;

  return (
    <ListItemText
      primaryTypographyProps={{ variant: 'h6' }}
      primary={<Link noTrack={noTrack} to={result.location}>{title}</Link>}
      secondary={description}
    />
  );
}

export { ProvisionedResourcesSearchResultListItemComponent };

// The predicate receives a SearchResult ({ type, document, ... }), not the document directly.
function isProvisionedResource(result: SearchResult): boolean {
  if (result.type === 'provisioned-resources') return true;
  // Capture software-catalog docs with enricher fields so we can suppress them —
  // Location kind and entities with duplicates in other indices are suppressed in the component.
  if (result.type === 'software-catalog') {
    const doc = result.document as ProvisionedSearchDocument;
    return !!(
      doc.provisioned_resource_ref ||
      doc.marketplace_product_id ||
      doc.kind === 'Template' ||
      doc.kind === 'Location'
    );
  }
  return false;
}

export const ProvisionedResourcesSearchResultListItem = searchPlugin.provide(
  createSearchResultListItemExtension({
    name: 'ProvisionedResourcesSearchResultListItem',
    component: async () => ProvisionedResourcesSearchResultListItemComponent,
    predicate: isProvisionedResource,
  }),
);
